<template>
  <div class="dev-pdf-parser-container">
    <div class="header-section">
      <h2>PDF 檢驗設備解析與預覽工具</h2>
      <p class="subtitle">自動識別 Catalyst One 與 ProCyte Dx 檢驗設備，解析檢驗項目、數值與單位並產生儲存專用 JSON</p>
    </div>

    <!-- 控制區 -->
    <div class="control-card">
      <div class="file-input-group">
        <label class="custom-file-upload">
          <input 
            type="file" 
            accept="application/pdf, .pdf" 
            @change="handleFileChange" 
            ref="fileInput"
          />
          選擇 PDF 檔案
        </label>
        <span class="selected-file-name" v-if="selectedFile">
          {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
        </span>
        <span class="no-file-text" v-else>尚未選擇檔案</span>
      </div>

      <div class="action-group">
        <button 
          class="btn-primary" 
          :disabled="!selectedFile || isLoading" 
          @click="handleParsePdf"
        >
          <span v-if="isLoading">讀取解析中...</span>
          <span v-else>讀取並解析 PDF</span>
        </button>
        <button 
          class="btn-secondary" 
          :disabled="!pdfDoc && parsedPages.length === 0" 
          @click="handleReset"
        >
          重置
        </button>
      </div>
    </div>

    <!-- 錯誤訊息提示 -->
    <div v-if="errorMessage" class="error-alert">
      {{ errorMessage }}
    </div>

    <!-- 摘要資訊 -->
    <div v-if="metaData" class="summary-card">
      <div class="summary-item">
        <span class="label">檔案名稱：</span>
        <span class="value">{{ metaData.fileName }}</span>
      </div>
      <div class="summary-item">
        <span class="label">檔案大小：</span>
        <span class="value">{{ metaData.fileSize }}</span>
      </div>
      <div class="summary-item">
        <span class="label">總頁數：</span>
        <span class="value">{{ metaData.totalPages }} 頁</span>
      </div>
      <div class="summary-item">
        <span class="label">識別設備數：</span>
        <span class="value text-highlight">{{ examReports.length }} 台</span>
      </div>
    </div>

    <!-- 結果區塊 -->
    <div v-if="parsedPages.length > 0" class="results-container">
      <div class="tabs-header">
        <button 
          :class="['tab-btn', { active: activeTab === 'storageJson' }]" 
          @click="activeTab = 'storageJson'"
        >
          💾 檢驗數據 (exam_reports JSON)
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'lab' }]" 
          @click="activeTab = 'lab'"
        >
          🩺 檢驗報告解析預覽
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'preview' }]" 
          @click="activeTab = 'preview'"
        >
          📄 原始頁面渲染
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'text' }]" 
          @click="activeTab = 'text'"
        >
          📝 純文字檢視
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'json' }]" 
          @click="activeTab = 'json'"
        >
          📦 PDF 原始 JSON
        </button>
      </div>

      <!-- Tab 1: 儲存用 JSON 格式分頁 -->
      <div v-show="activeTab === 'storageJson'" class="tab-content">
        <div class="json-header-toolbar">
          <div class="toolbar-info">
            <span class="tag-status">已解析 {{ examReports.length }} 份設備檢驗紀錄</span>
            <span class="text-muted text-small ms-2">純設備檢驗項目、數值與單位之 exam_reports 陣列結構</span>
          </div>
          <div class="toolbar-actions">
            <button class="btn-copy" @click="copyText(JSON.stringify(examReports, null, 2))">📋 複製 exam_reports JSON</button>
            <button class="btn-copy" @click="downloadJson(examReports, 'exam_reports.json')">⬇️ 下載 JSON 檔案</button>
          </div>
        </div>

        <div class="storage-json-wrapper">
          <pre class="json-content storage-json-view">{{ JSON.stringify(examReports, null, 2) }}</pre>
        </div>
      </div>

      <!-- Tab 2: 獨立檢驗報告解析預覽元件 -->
      <div v-show="activeTab === 'lab'" class="tab-content">
        <LabExamReportPreview :storageData="examReports" />
      </div>

      <!-- Tab 3: Canvas 頁面預覽 -->
      <div v-show="activeTab === 'preview'" class="tab-content preview-tab">
        <div 
          v-for="page in parsedPages" 
          :key="`canvas-page-${page.pageNumber}`" 
          class="canvas-page-wrapper"
        >
          <div class="page-header">
            <h3>第 {{ page.pageNumber }} 頁預覽</h3>
          </div>
          <canvas :id="`pdf-canvas-page-${page.pageNumber}`" class="pdf-canvas"></canvas>
        </div>
      </div>

      <!-- Tab 4: 文字解析檢視 -->
      <div v-show="activeTab === 'text'" class="tab-content">
        <div class="page-block" v-for="page in parsedPages" :key="`text-page-${page.pageNumber}`">
          <div class="page-header">
            <h3>第 {{ page.pageNumber }} 頁 (共 {{ page.items.length }} 個文字區塊)</h3>
            <button class="btn-copy" @click="copyText(page.fullText)">複製此頁純文字</button>
          </div>
          <div class="page-text-content">
            <pre>{{ page.fullText || '(此頁無可解析的純文字內容)' }}</pre>
          </div>
        </div>
      </div>

      <!-- Tab 5: PDF 原始 JSON 結構 -->
      <div v-show="activeTab === 'json'" class="tab-content">
        <div class="json-wrapper">
          <div class="json-actions">
            <button class="btn-copy" @click="copyText(JSON.stringify(parsedPages, null, 2))">複製完整 PDF.js JSON</button>
          </div>
          <pre class="json-content">{{ JSON.stringify(parsedPages, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LabExamReportPreview from '@/components/labExamReportPreview.vue';
import { formatFileSize, parsePdfFile } from '@/assets/js/tools/pdfLabParser';

export default {
  name: 'DevPdfParser',
  components: {
    LabExamReportPreview
  },
  data() {
    return {
      selectedFile: null,
      isLoading: false,
      errorMessage: '',
      pdfDoc: null,
      parsedPages: [],
      examReports: [],
      metaData: null,
      activeTab: 'storageJson'
    };
  },
  methods: {
    formatFileSize,

    /**
     * 檔案選取變更處理
     * @param {Event} event - 檔案變更事件
     */
    handleFileChange(event) {
      try {
        const files = event.target.files;
        if (!files || files.length === 0) {
          return;
        }
        this.selectedFile = files[0];
        this.errorMessage = '';
      } catch (error) {
        console.error('[DevPdfParser] handleFileChange error:', error);
      }
    },

    /**
     * 重置目前資料與狀態
     */
    handleReset() {
      try {
        this.selectedFile = null;
        this.pdfDoc = null;
        this.parsedPages = [];
        this.examReports = [];
        this.metaData = null;
        this.errorMessage = '';
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      } catch (error) {
        console.error('[DevPdfParser] handleReset error:', error);
      }
    },

    /**
     * 讀取並解析 PDF 核心方法 (直接提取 exam_reports)
     */
    async handleParsePdf() {
      if (!this.selectedFile) return;

      this.isLoading = true;
      this.errorMessage = '';
      this.parsedPages = [];
      this.examReports = [];

      try {
        const result = await parsePdfFile(this.selectedFile);

        this.pdfDoc = result.pdfDoc;
        this.parsedPages = result.parsedPages;
        this.examReports = result.exam_reports || [];
        this.metaData = result.metaData;
        this.activeTab = 'storageJson';

        this.$nextTick(() => {
          this.renderAllPagesCanvas();
        });
      } catch (error) {
        console.error('[DevPdfParser] handleParsePdf error:', error);
        this.errorMessage = `PDF 解析失敗: ${error.message || '未知錯誤'}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 渲染所有頁面至 Canvas
     */
    async renderAllPagesCanvas() {
      if (!this.pdfDoc) return;
      try {
        for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
          const canvas = document.getElementById(`pdf-canvas-page-${pageNum}`);
          if (!canvas) continue;

          const page = await this.pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });
          const context = canvas.getContext('2d');
          const dpr = window.devicePixelRatio || 1;

          canvas.width = viewport.width * dpr;
          canvas.height = viewport.height * dpr;
          canvas.style.width = '100%';
          canvas.style.maxWidth = `${viewport.width}px`;
          canvas.style.height = 'auto';

          context.scale(dpr, dpr);

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };
          await page.render(renderContext).promise;
        }
      } catch (error) {
        console.error('[DevPdfParser] renderAllPagesCanvas error:', error);
      }
    },

    /**
     * 複製文字至剪貼簿
     * @param {string} text - 欲複製文字
     */
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        alert('已複製到剪貼簿！');
      } catch (error) {
        console.error('[DevPdfParser] copyText error:', error);
      }
    },

    /**
     * 下載 JSON 檔案
     * @param {Object} data - 欲下載的物件
     * @param {string} fileName - 檔名
     */
    downloadJson(data, fileName = 'download.json') {
      try {
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('[DevPdfParser] downloadJson error:', error);
      }
    }
  }
};
</script>

<style scoped>
.dev-pdf-parser-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #333;
}

.header-section {
  margin-bottom: 24px;
}

.header-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}

.subtitle {
  font-size: 14px;
  color: #666;
}

.control-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.file-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.custom-file-upload {
  display: inline-block;
  padding: 8px 16px;
  cursor: pointer;
  background-color: #f0f4f8;
  color: #1e3a8a;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.custom-file-upload:hover {
  background-color: #e2e8f0;
}

.custom-file-upload input[type="file"] {
  display: none;
}

.selected-file-name {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}

.no-file-text {
  font-size: 14px;
  color: #94a3b8;
}

.action-group {
  display: flex;
  gap: 10px;
}

.btn-primary {
  background-color: #2563eb;
  color: #ffffff;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.btn-primary:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.btn-outline {
  background-color: #ffffff;
  color: #2563eb;
  border: 1px solid #2563eb;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-outline:hover {
  background-color: #eff6ff;
}

.btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-alert {
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.summary-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-item .label {
  color: #64748b;
  font-size: 13px;
}

.summary-item .value {
  font-weight: 600;
  color: #1e293b;
  margin-left: 6px;
}

.text-highlight {
  color: #2563eb !important;
}

.results-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.tab-btn {
  padding: 12px 20px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  background-color: #ffffff;
}

.tab-content {
  padding: 20px;
}

/* 儲存用 JSON 頂部工具列 */
.json-header-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-status {
  background: #2563eb;
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.storage-json-view {
  background: #090d16 !important;
  border: 1px solid #1e293b;
  color: #38bdf8 !important;
}

/* 純文字與 Canvas */
.page-block {
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.page-header h3 {
  font-size: 16px;
  color: #334155;
  margin: 0;
}

.btn-copy {
  padding: 6px 12px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  color: #334155;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-copy:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.page-text-content pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8fafc;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #1e293b;
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}

.preview-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.canvas-page-wrapper {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
}

.pdf-canvas {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #ffffff;
  border-radius: 4px;
}

.json-wrapper {
  position: relative;
}

.json-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 10px;
}

.json-content {
  background: #0f172a;
  color: #f8fafc;
  padding: 16px;
  border-radius: 6px;
  font-size: 12px;
  max-height: 600px;
  overflow-y: auto;
}
</style>
